import React, { useEffect, useState, useCallback } from 'react';
import { useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { authSelector } from '../features/auth/slice';
import { RefreshCw, ExternalLink, CheckCircle2, AlertCircle, Loader2 } from 'lucide-react';
import Pagination from '../components/common/Pagination';

const PAGE_SIZE = 20;

export default function PromotionReportPage() {
  const { me } = useSelector(authSelector.selectAuthState);
  const navigate = useNavigate();

  const [rows, setRows] = useState([]);
  const [syncedAt, setSyncedAt] = useState(null);
  const [loading, setLoading] = useState(false);
  const [syncing, setSyncing] = useState(false);
  const [page, setPage] = useState(1);

  useEffect(() => {
    if (me && me.adminYn !== 'Y') navigate('/dashboard', { replace: true });
  }, [me, navigate]);

  const fetchAll = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await axios.get('/api/promotion-report/all');
      setRows(data.rows || []);
      setSyncedAt(data.syncedAt);
      setPage(1);
    } catch (err) {
      alert(err.response?.data?.message || '조회 실패');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  const handleSync = async () => {
    setSyncing(true);
    try {
      const { data } = await axios.post('/api/promotion-report/sync');
      alert(`동기화 완료: 총 ${data.total}건 / 매칭 ${data.matched}건`);
      await fetchAll();
    } catch (err) {
      alert(err.response?.data?.message || '동기화 실패');
    } finally {
      setSyncing(false);
    }
  };

  const totalPages = Math.ceil(rows.length / PAGE_SIZE);
  const paged = rows.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE);

  return (
    <div>
      {/* 헤더 */}
      <div className="flex mb-8 justify-center items-center">
        <div className="text-xl sm:text-2xl font-bold text-white tracking-tight">홍보리포트</div>
      </div>

      {/* 통계 카드 */}
      <div className="grid grid-cols-4 gap-4 mb-6">
        {[
          { label: '전체', value: rows.length, color: 'text-slate-200' },
          { label: '공개 완료', value: rows.filter((r) => r.isPublished).length, color: 'text-green-400' },
          { label: '링크 있음', value: rows.filter((r) => r.linkUrl).length, color: 'text-sky-400' },
          { label: '권리사 수', value: new Set(rows.map((r) => r.clientId)).size, color: 'text-purple-400' },
        ].map((s) => (
          <div key={s.label} className="bg-white/[0.03] rounded-xl border border-white/[0.06] p-4 text-center">
            <div className={`text-2xl font-bold ${s.color}`}>{s.value}</div>
            <div className="text-xs text-slate-500 mt-1">{s.label}</div>
          </div>
        ))}
      </div>

      {loading ? (
        <div className="flex justify-center py-20"><Loader2 size={32} className="animate-spin text-slate-400" /></div>
      ) : (
        <>
          <div className="bg-white/[0.03] rounded-xl border border-white/[0.06] overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full text-sm min-w-[700px]">
                <thead>
                  <tr className="bg-white/[0.03] text-xs text-slate-400 uppercase border-b border-white/[0.06]">
                    <th className="px-4 py-3 text-left whitespace-nowrap">아티스트</th>
                    <th className="px-4 py-3 text-left whitespace-nowrap">곡명</th>
                    <th className="px-4 py-3 text-left whitespace-nowrap">채널</th>
                    <th className="px-4 py-3 text-left whitespace-nowrap">유형</th>
                    <th className="px-4 py-3 text-left whitespace-nowrap">링크</th>
                    <th className="px-4 py-3 text-left whitespace-nowrap">게시일</th>
                    <th className="px-4 py-3 text-center whitespace-nowrap">공개</th>
                  </tr>
                </thead>
                <tbody>
                  {paged.length === 0 && (
                    <tr>
                      <td colSpan={7} className="text-center py-12 text-slate-500">
                        데이터가 없습니다. 구글 시트 가져오기를 실행하세요.
                      </td>
                    </tr>
                  )}
                  {paged.map((r) => (
                    <tr key={r.id} className="border-t border-white/[0.04] hover:bg-white/[0.03] transition-colors">
                      <td className="px-4 py-2.5 font-medium text-white whitespace-nowrap">{r.artist || '-'}</td>
                      <td className="px-4 py-2.5 text-slate-300 whitespace-nowrap">{r.songName || '-'}</td>
                      <td className="px-4 py-2.5 text-slate-400 whitespace-nowrap">{r.channel || '-'}</td>
                      <td className="px-4 py-2.5 whitespace-nowrap">
                        <span className="px-2 py-0.5 rounded-full text-xs bg-sky-500/15 text-sky-300">
                          {r.videoType || '-'}
                        </span>
                      </td>
                      <td className="px-4 py-2.5 max-w-[200px]">
                        {r.linkUrl ? (
                          <a href={r.linkUrl} target="_blank" rel="noopener noreferrer"
                            className="flex items-center gap-1 text-sky-400 hover:underline">
                            <ExternalLink size={12} className="shrink-0" />
                            <span className="truncate text-xs">{r.linkText || r.linkUrl}</span>
                          </a>
                        ) : (
                          <span className="text-slate-500 text-xs truncate block">{r.linkText || '-'}</span>
                        )}
                      </td>
                      <td className="px-4 py-2.5 text-slate-400 whitespace-nowrap">{r.publishDate || '-'}</td>
                      <td className="px-4 py-2.5 text-center">
                        {r.isPublished
                          ? <CheckCircle2 size={16} className="text-green-400 mx-auto" />
                          : <AlertCircle size={16} className="text-slate-600 mx-auto" />}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          {totalPages > 1 && (
            <Pagination currentPage={page} totalPages={totalPages} onPageChange={setPage} />
          )}

          <div className="flex items-center justify-between mt-5">
            <span className="text-xs text-slate-500">
              {syncedAt && `마지막 동기화: ${new Date(syncedAt).toLocaleString('ko-KR')}`}
            </span>
            <button
              onClick={handleSync}
              disabled={syncing}
              className={`flex items-center gap-2 px-5 py-2 rounded-xl text-white text-sm font-semibold transition-colors ${
                syncing ? 'bg-slate-600 cursor-not-allowed' : 'bg-sky-500 hover:bg-sky-600'
              }`}>
              {syncing ? <Loader2 size={15} className="animate-spin" /> : <RefreshCw size={15} />}
              {syncing ? '동기화 중...' : '구글 시트 가져오기'}
            </button>
          </div>
        </>
      )}
    </div>
  );
}
